import React, { useEffect, useState } from 'react';
import { useClient } from '@/context/ClientContext';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import TrendChart from '@/components/TrendChart';
import ScoreGrid from '@/components/ScoreGrid';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Sparkles, TrendingUp, Award, Loader2, Workflow, Rocket } from 'lucide-react';
import { DASHBOARD } from '@/constants/testIds';
import { toast } from 'sonner';

const RANGES = [
  { v: '7', label: '7D' }, { v: '30', label: '30D' }, { v: '90', label: '90D' }, { v: '365', label: '1Y' }, { v: 'all', label: 'ALL' },
];

export default function Dashboard() {
  const { activeClient, clients, loading } = useClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [range, setRange] = useState('90');
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);

  // Auto-redirect clients whose intake is incomplete
  useEffect(() => {
    if (activeClient && activeClient.intake_status === 'pending' && user?.role === 'client') {
      navigate(`/intake/${activeClient.client_id}`, { replace: true });
    }
  }, [activeClient, user, navigate]);

  useEffect(() => {
    if (!activeClient) return;
    api.get(`/dashboard/${activeClient.client_id}`).then(r => setData(r.data));
  }, [activeClient]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  if (!loading && clients.length === 0) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <Sparkles className="h-5 w-5" />
        </div>
        <h2 className="mt-6 font-display text-3xl tracking-tight">Welcome to AuthorityOS™</h2>
        <p className="mt-3 text-muted-foreground">Onboard your first client to activate the agentic swarm.</p>
        <Button className="mt-8" onClick={() => navigate('/onboarding')}>Start onboarding</Button>
      </div>
    );
  }

  if (!data) return <div className="text-sm text-muted-foreground">Loading dashboard…</div>;

  const intakePending = activeClient?.intake_status === 'pending';
  const aeoRunning = activeClient?.aeo_status === 'running';
  const history = filterHistory(data.history || [], range);

  const generateBrief = async () => {
    setBriefLoading(true);
    setBrief(null);
    try {
      const { data } = await api.post(`/manus/brief/${activeClient.client_id}`);
      setBrief(data);
    } catch (e) {
      toast.error('Executive brief failed');
    } finally {
      setBriefLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {intakePending && (
        <div data-testid="dashboard-intake-cta" className="rounded-xl border border-primary/40 bg-primary/5 p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Rocket className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl">Begin the AEO process for {activeClient.company_name}</div>
            <p className="mt-1 text-sm text-muted-foreground">
              We need your company brief to calibrate the swarm. The full Authority Engineering (AEO) assessment runs automatically the moment you submit.
            </p>
          </div>
          <Button
            data-testid="dashboard-begin-aeo"
            onClick={() => navigate(`/intake/${activeClient.client_id}`)}
            size="lg"
          >
            <Sparkles className="h-4 w-4 mr-2" /> Begin AEO process
          </Button>
        </div>
      )}

      {aeoRunning && !intakePending && (
        <div className="rounded-xl border border-primary/30 bg-card p-4 flex items-center gap-3 text-sm">
          <Loader2 className="h-4 w-4 text-primary animate-spin" />
          <span>The AEO assessment is running for {activeClient.company_name}. Results will appear in the Assessments tab in about a minute.</span>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Hero Score */}
        <div data-testid={DASHBOARD.scoreCard} className="flex-1 rounded-xl border border-border bg-card p-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Authority Score™</div>
              <div className="mt-1 font-display text-2xl">{data.company_name}</div>
            </div>
            <Badge data-testid={DASHBOARD.certificationBadge} variant="outline" className="text-xs">
              <Award className="h-3 w-3 mr-1 text-primary" /> {data.certification}
            </Badge>
          </div>
          <div className="mt-8 flex items-end gap-6">
            <div className="score-num text-7xl lg:text-8xl text-primary tracking-tight">{data.authority_score}</div>
            <div className="pb-4">
              <div className="text-3xl font-display">{data.letter_grade}</div>
              <div className="text-xs text-muted-foreground">letter grade</div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => navigate('/assessments')}>
              <Sparkles className="h-4 w-4 mr-1" /> Run AEO assessment
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/simulator')}>
              <TrendingUp className="h-4 w-4 mr-1" /> Simulate uplift
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid={DASHBOARD.manusBriefBtn}
              onClick={generateBrief}
              disabled={briefLoading}
            >
              {briefLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Workflow className="h-4 w-4 mr-1" />}
              Weekly executive brief
            </Button>
          </div>
        </div>

        {/* Trend */}
        <div data-testid={DASHBOARD.trendChart} className="flex-1 rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Authority Trend</div>
              <div className="font-display text-lg">Continuous measurement</div>
            </div>
            <Tabs value={range} onValueChange={setRange}>
              <TabsList>
                {RANGES.map(r => <TabsTrigger key={r.v} value={r.v}>{r.label}</TabsTrigger>)}
              </TabsList>
            </Tabs>
          </div>
          <TrendChart history={history} />
        </div>
      </div>

      {brief && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <Workflow className="h-3 w-3" /> Executive Brief
          </div>
          <h3 className="mt-2 font-display text-2xl">{brief.headline}</h3>
          <div className="mt-5 grid md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">This week priorities</div>
              <ul className="mt-2 space-y-1 text-sm">
                {(brief.this_week_priorities || []).map((p, i) => (
                  <li key={i} className="flex gap-2"><span className="text-primary">→</span><span>{p}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Agent delegations</div>
              <ul className="mt-2 space-y-1 text-sm">
                {(brief.delegations || []).map((d, i) => (
                  <li key={i}><span className="font-medium">{d.agent}:</span> <span className="text-muted-foreground">{d.task}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Forecast & risks</div>
              <p className="mt-2 text-sm text-muted-foreground">{brief.forecast_30d}</p>
              <ul className="mt-2 space-y-1 text-sm">
                {(brief.risk_flags || []).map((r, i) => (
                  <li key={i} className="flex gap-2"><span className="text-amber-500">⚠</span><span>{r}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Sub-scores */}
      <div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Sub-scores</div>
            <h3 className="font-display text-2xl tracking-tight">Authority signals</h3>
          </div>
          <div className="text-xs text-muted-foreground">17 weighted signals</div>
        </div>
        <ScoreGrid testid={DASHBOARD.subScoreGrid} scores={data.scores || {}} />
      </div>
    </div>
  );
}

function filterHistory(history, range) {
  if (range === 'all') return history;
  const days = parseInt(range, 10);
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return history.filter(h => new Date(h.date).getTime() >= cutoff);
}
